/**
 * Work or Expert: which interface this person is using.
 *
 * PROVENANCE: copied from C:\Aris\frontend\src\hooks\useWorkMode.ts @ 5d37545 (it imports only React): a module
 * store read through useSyncExternalStore, so the switch in the header and the shell agree on every render;
 * storage wrapped, so a blocked localStorage still leaves a working switch. CHANGES: the storage key is olivia's;
 * isWorkPath and the Work/Expert equivalents follow Olivia's URLs (design 10.2: /c/:client/work in Work mode,
 * /c/:client in Expert mode; each Work view has an Expert equivalent); and Expert is the default (the owner's decision
 * D-13 of the expert review, Horizon's rule D-W1: Expert stays the default until Work answers most of its questions),
 * so a person who has never chosen opens on the screens that are built. A remembered choice is kept.
 */

import { useCallback, useSyncExternalStore } from 'react'

export type WorkMode = 'work' | 'expert'

const STORAGE_KEY = 'olivia.mode'

/** The mode a stored value names; anything else, nothing stored included, is the default: Expert. */
export const DEFAULT_MODE: WorkMode = 'expert'

export function parseWorkMode(value: string | null | undefined): WorkMode {
  return value === 'work' ? 'work' : value === 'expert' ? 'expert' : DEFAULT_MODE
}

function stored(): WorkMode {
  if (typeof window === 'undefined') return DEFAULT_MODE
  try {
    return parseWorkMode(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return DEFAULT_MODE
  }
}

let current: WorkMode = stored()
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function setWorkMode(mode: WorkMode): void {
  current = mode
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // The preference simply does not persist; the switch still works.
  }
  for (const listener of listeners) listener()
}

export function useWorkMode(): [WorkMode, (mode: WorkMode) => void] {
  const mode = useSyncExternalStore(subscribe, () => current, () => DEFAULT_MODE)
  const set = useCallback((value: WorkMode) => setWorkMode(value), [])
  return [mode, set]
}

/** Work view → its Expert equivalent, and back (design 10.2). */
const EQUIVALENTS: [work: string, expert: string][] = [
  ['work', ''],
  ['work/deadlines', 'cycle'],
  ['work/todo', 'registers/actions'],
  ['work/happened', 'audit'],
]

/** The same view of the same client in the other mode. Pure. */
export function equivalentPath(pathname: string, to: WorkMode): string {
  const m = /^\/c\/([a-z0-9-]+)(?:\/(.*))?$/.exec(pathname)
  if (!m) return pathname
  const code = m[1]
  const rest = (m[2] ?? '').replace(/\/+$/, '')
  if (to === 'expert') {
    const hit = EQUIVALENTS.find(([w]) => w === rest)
    if (hit) return hit[1] ? `/c/${code}/${hit[1]}` : `/c/${code}`
    return rest.startsWith('work') ? `/c/${code}` : pathname
  }
  const hit = EQUIVALENTS.find(([, e]) => e === rest)
  if (hit) return `/c/${code}/${hit[0]}`
  return pathname
}

/** A Work view of a client (/c/:client/work…): the switch says Work there, whatever was stored (IP0). Pure. */
export function isWorkView(pathname: string): boolean {
  return /^\/c\/[a-z0-9-]+\/work(\/|$)/.test(pathname)
}
